import { useState } from 'react';
import { ArrowDown, CheckCircle2, Download, FileText, Loader2, XCircle } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { hideProducts } from '@/hooks/useInventory';
import { uploadProductFile } from '@services/inventory';
import { cn } from '@/lib/utils';

/**
 * Bulk hide / unhide dialog. Takes a newline-separated list of barcodes and
 * calls the shared `hide-products` handler. Channel-scoped hiding (the
 * "selected channels" option) is a follow-up — for now both options map to
 * an all-channels enable/disable.
 */
function BarcodeActionDialog({ open, onOpenChange, shopId, title, cta, enabled, onDone }) {
	const [text, setText] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);

	const barcodes = text
		.split('\n')
		.map((b) => b.trim())
		.filter(Boolean);

	const submit = async () => {
		if (!shopId || !barcodes.length) return;
		setBusy(true);
		setError(null);
		try {
			await hideProducts(shopId, { barcodes, enabled });
			setText('');
			onOpenChange(false);
			onDone?.();
		} catch (e) {
			setError(e?.response?.data?.message ?? e.message ?? 'Something went wrong.');
		} finally {
			setBusy(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-2xl sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-xl font-bold">{title}</DialogTitle>
				</DialogHeader>
				<div className="space-y-5">
					<div>
						<div className="mb-2 text-sm font-bold text-foreground">Enter one barcode per line</div>
						<textarea
							value={text}
							onChange={(e) => setText(e.target.value)}
							placeholder={'barcode001\nbarcode002\nbarcode003'}
							rows={6}
							className="w-full resize-none rounded-xl border border-border bg-background px-4 py-3 font-mono text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
						/>
					</div>
					{error ? <p className="text-sm text-destructive">{error}</p> : null}
					<button
						type="button"
						disabled={busy || !barcodes.length}
						onClick={submit}
						className="inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
					>
						{busy ? 'Working…' : `${cta} (${barcodes.length})`}
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

export function HideProductsDialog({ open, onOpenChange, shopId, onDone }) {
	return (
		<BarcodeActionDialog
			open={open}
			onOpenChange={onOpenChange}
			shopId={shopId}
			title="Hide products"
			cta="Hide products"
			enabled={false}
			onDone={onDone}
		/>
	);
}

export function UnhideProductsDialog({ open, onOpenChange, shopId, onDone }) {
	return (
		<BarcodeActionDialog
			open={open}
			onOpenChange={onOpenChange}
			shopId={shopId}
			title="Unhide products"
			cta="Unhide products"
			enabled
			onDone={onDone}
		/>
	);
}

const EXAMPLE_CSV = `barcode,title,description,brand,image_url,link,menu_categories,tax_percentage,price_markup_deliveroo,price_markup_uber_eats,price_markup_just_eat,classifications,unit_pricing_measure,unit_pricing_base_measure
123456,Product Name,Product Description,Brand Name,https://example.com/image.jpg,https://example.com/product,"Category 1, Category 2",20,15,15,15,"pharmaceuticals_aspirin",500g,1kg`;

// Built once at module load — the CSV is static, so minting a fresh blob URL
// (and leaking the previous one) on every render would be wasteful.
const EXAMPLE_CSV_URL = URL.createObjectURL(new Blob([EXAMPLE_CSV], { type: 'text/csv;charset=utf-8;' }));

/**
 * Bulk product-data upload. Mirrors the retailers dashboard's
 * ProductUploadModal — a CSV goes to S3 via the `get-product-upload-url`
 * presigned URL, then `validate-product-upload` kicks off processing.
 */
export function UploadProductDataDialog({ open, onOpenChange, organisationId, shopId }) {
	const [status, setStatus] = useState('idle'); // idle | uploading | success | error
	const [filename, setFilename] = useState('');
	const [error, setError] = useState(null);
	const [dragging, setDragging] = useState(false);

	const reset = () => {
		setStatus('idle');
		setFilename('');
		setError(null);
		setDragging(false);
	};

	const handleClose = (v) => {
		if (status === 'uploading') return;
		onOpenChange(v);
		if (!v) reset();
	};

	const upload = async (file) => {
		if (!file || !organisationId || !shopId) return;
		setFilename(file.name);
		setStatus('uploading');
		setError(null);
		try {
			await uploadProductFile(file, organisationId, shopId);
			setStatus('success');
		} catch (e) {
			setError(e?.message ?? 'Upload failed.');
			setStatus('error');
		}
	};

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="rounded-2xl sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-xl font-bold">Upload product data</DialogTitle>
				</DialogHeader>
				<div className="space-y-5">
					{status === 'idle' ? (
						<>
							<div className="space-y-3 text-sm text-foreground">
								<p>
									You can add product data for multiple barcodes at once by uploading a .csv file containing the barcodes,
									along with any extra product information.
								</p>
								<p>
									The example file below contains all the fields it is possible to add, but your file must have a{' '}
									<span className="font-bold">barcode</span> field.
								</p>
							</div>
							<a
								href={EXAMPLE_CSV_URL}
								download="product-data-template.csv"
								className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
							>
								Download example file <Download className="h-4 w-4" />
							</a>
							<label
								onDragOver={(e) => {
									e.preventDefault();
									setDragging(true);
								}}
								onDragLeave={() => setDragging(false)}
								onDrop={(e) => {
									e.preventDefault();
									setDragging(false);
									upload(e.dataTransfer.files?.[0]);
								}}
								className={cn(
									'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 text-center transition',
									dragging
										? 'border-primary bg-muted/50'
										: 'border-border bg-muted/30 hover:border-primary/60 hover:bg-muted/50'
								)}
							>
								<ArrowDown className="h-10 w-10 text-muted-foreground" strokeWidth={1.5} />
								<div className="text-sm text-foreground">
									Drop here or <span className="font-semibold text-primary">browse</span>
								</div>
								<input
									type="file"
									accept=".csv,text/csv"
									className="hidden"
									onChange={(e) => upload(e.target.files?.[0])}
								/>
							</label>
						</>
					) : null}

					{status === 'uploading' ? (
						<div className="flex items-center gap-3 rounded-xl bg-muted/50 px-4 py-4 text-sm">
							<Loader2 className="h-5 w-5 animate-spin text-primary" />
							<div>
								<div className="font-semibold text-foreground">{filename}</div>
								<div className="text-muted-foreground">Uploading and validating your file…</div>
							</div>
						</div>
					) : null}

					{status === 'success' ? (
						<div className="flex items-start gap-3 rounded-xl bg-success-soft px-4 py-4 text-sm">
							<CheckCircle2 className="h-5 w-5 shrink-0 text-success-soft-foreground" />
							<div>
								<div className="font-semibold text-success-soft-foreground">File uploaded</div>
								<div className="text-muted-foreground">We'll email you once your products have been processed.</div>
							</div>
						</div>
					) : null}

					{status === 'error' ? (
						<div className="space-y-3">
							<div className="flex items-start gap-3 rounded-xl bg-destructive/5 px-4 py-4 text-sm">
								<XCircle className="h-5 w-5 shrink-0 text-destructive" />
								<div>
									<div className="font-semibold text-destructive">Unable to process file</div>
									<div className="text-muted-foreground">
										{error === 'File validation failed'
											? 'Make sure your file has a barcode header and try again.'
											: 'The file failed to upload. Please try again.'}
									</div>
								</div>
							</div>
							<button
								type="button"
								onClick={reset}
								className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
							>
								<FileText className="h-4 w-4" /> Try another file
							</button>
						</div>
					) : null}
				</div>
			</DialogContent>
		</Dialog>
	);
}

const HIDE_NO_TITLE_KEY = 'unify.inventory.hideWithoutTitles';

export function HideWithoutTitleDialog({ open, onOpenChange }) {
	const [enabled, setEnabled] = useState(() => {
		try {
			return window.localStorage.getItem(HIDE_NO_TITLE_KEY) === '1';
		} catch {
			return false;
		}
	});

	const toggle = () => {
		setEnabled((v) => {
			const next = !v;
			try {
				window.localStorage.setItem(HIDE_NO_TITLE_KEY, next ? '1' : '0');
			} catch {
				/* ignore */
			}
			return next;
		});
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="rounded-2xl sm:max-w-xl">
				<DialogHeader>
					<DialogTitle className="text-xl font-bold">Hide products without titles</DialogTitle>
				</DialogHeader>
				<div className="space-y-5">
					<p className="text-sm leading-relaxed text-muted-foreground">
						Products that only have a barcode (missing a title) are hidden from your Storefront and feed channels. Google Local
						Listings is not affected — Google supplements its own product data. These products stay visible here so you can add
						titles.
					</p>
					<div className="flex items-center justify-between gap-4 rounded-xl bg-muted/60 px-4 py-3">
						<span className="text-sm font-semibold text-foreground">Hide products without titles</span>
						<button
							type="button"
							role="switch"
							aria-checked={enabled}
							onClick={toggle}
							className={cn(
								'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition',
								enabled ? 'bg-primary' : 'bg-border'
							)}
						>
							<span
								className={cn(
									'inline-block h-5 w-5 transform rounded-full bg-white shadow transition',
									enabled ? 'translate-x-[22px]' : 'translate-x-0.5'
								)}
							/>
						</button>
					</div>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						className="inline-flex items-center justify-center rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition hover:bg-foreground/90"
					>
						Done
					</button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
